import { CliCommandSpec } from 'types/cli';

/** Terminal built-ins that never reach the server. */
export const BUILTINS = ['help', 'clear'];
export const POD_BUILTINS = ['cd', 'pwd'];

// Mirrors cloud-server's lineParser: on a pod tab a bare first word is an exec command.
// Exported so the transcript's highlighter classifies a line the same way completion does.
export const IMPLIED_GROUPS: Record<string, string> = {
  tail: 'logs',
  cat: 'logs',
};
const POD_GROUPS = new Set(['exec', 'logs']);
const BUCKET_GROUPS = new Set(['tenant', 'archived-logs']);
// The options every node-aware command accepts on top of what its usage lists.
const NODE_OPTIONS = ['--node', '--node-number', '--self-managed-name'];

const uniqueGroups = (commands: CliCommandSpec[]) => [
  ...new Set(commands.map(command => command.category)),
];

/** The group a bare first word belongs to on a pod tab, in the order lineParser's implyGroup tries them. */
const impliedGroupFor = (
  word: string,
  tabGroupCommands: string[],
  tabGroup: string | undefined,
  runnable: (group: string) => string[],
) => {
  if (POD_BUILTINS.includes(word)) return 'builtin';
  if (tabGroupCommands.includes(word)) return tabGroup;
  if (IMPLIED_GROUPS[word]) return IMPLIED_GROUPS[word];
  return runnable('exec').includes(word) ? 'exec' : undefined;
};

const longestCommonPrefix = (values: string[]) => {
  if (!values.length) return '';
  return values.reduce((prefix, value) => {
    let index = 0;
    while (
      index < prefix.length &&
      index < value.length &&
      prefix[index] === value[index]
    )
      index += 1;
    return prefix.slice(0, index);
  });
};

/** `find DIRECTORY [--name PATTERN] [--type f|d]` -> { '--name': [], '--type': ['f', 'd'] }. */
export const optionsFromUsage = (usage: string) => {
  const options: Record<string, string[]> = {};
  for (const match of usage.matchAll(/(--[a-z][\w-]*)(?:[ =]([^\s\]]+))?/g)) {
    const [, name, value] = match;
    options[name] = value && value.includes('|') ? value.split('|') : [];
  }
  return options;
};

export interface Completion {
  line: string;
  suggestions: string[];
}

export interface CompletionContext {
  /** On a pod tab, exec (and logs tail/cat) commands complete bare and paths resolve on the pod. */
  podTab: boolean;
  /** The tab's node. When it is also a command group (osaka, zaghlul, ...) its commands complete bare too. */
  tabGroup?: string;
  /** Values for `--node`, from the pods the terminal knows about. */
  nodeNames?: string[];
  /** Values for `--self-managed-name`. */
  selfManagedNames?: string[];
  /** Entries of a directory on the pod, relative to the tab's working directory; directories carry a trailing `/`. */
  listPodDirectory?(directory: string): Promise<string[]>;
  /** Entries of a directory in a bucket group (`tenant`, `archived-logs`), same shape. */
  listBucketDirectory?(group: string, directory: string): Promise<string[]>;
}

const noCompletion = (line: string): Completion => ({ line, suggestions: [] });

/** The completed line for one of `matches`, or the longest shared prefix with the rest listed. */
const settle = (
  head: string,
  matches: string[],
  partial: string,
  trailing = ' ',
): Completion => {
  if (!matches.length) return noCompletion(head + partial);
  if (matches.length === 1)
    return { line: `${head}${matches[0]}${trailing}`, suggestions: [] };
  return { line: head + longestCommonPrefix(matches), suggestions: matches };
};

const completePath = async (
  head: string,
  partial: string,
  list: (directory: string) => Promise<string[]>,
) => {
  const slash = partial.lastIndexOf('/');
  const directory = slash === -1 ? '' : partial.slice(0, slash + 1);
  const base = slash === -1 ? partial : partial.slice(slash + 1);

  let entries: string[];
  try {
    entries = await list(directory);
  } catch {
    return noCompletion(head + partial);
  }
  // Dotfiles only when asked for, as a shell does.
  const matches = entries.filter(
    entry =>
      entry.startsWith(base) &&
      (base.startsWith('.') || !entry.startsWith('.')),
  );
  if (matches.length === 1) {
    const [match] = matches;
    return {
      line: `${head}${directory}${match}${match.endsWith('/') ? '' : ' '}`,
      suggestions: [],
    };
  }
  return {
    line:
      head + directory + (matches.length ? longestCommonPrefix(matches) : base),
    suggestions: matches,
  };
};

/** Splits the line into what is settled and the word being typed. */
const splitLine = (line: string) => {
  const endsWithSpace = /\s$/.test(line);
  const tokens = line.split(/\s+/).filter(Boolean);
  const partial = endsWithSpace ? '' : (tokens[tokens.length - 1] ?? '');
  const settled = endsWithSpace ? tokens : tokens.slice(0, -1);
  const head =
    endsWithSpace || !tokens.length
      ? line
      : line.slice(0, line.length - partial.length);
  return { settled, partial, head };
};

export async function completeLine(
  line: string,
  commands: CliCommandSpec[],
  context: CompletionContext = { podTab: false },
): Promise<Completion> {
  const { settled, partial, head } = splitLine(line);
  const groups = uniqueGroups(commands);
  // Only what this user can actually run: not browser-unsupported, and not above their role.
  const runnable = (group: string) =>
    commands
      .filter(
        command =>
          command.category === group && !command.unsupported && command.allowed,
      )
      .map(command => command.command);

  // A tab whose pod is also a command group runs that group's commands bare, the way lineParser's
  // implyGroup does -- `service-state` on the zaghlul tab is `zaghlul service-state`.
  const tabGroup =
    context.tabGroup && groups.includes(context.tabGroup)
      ? context.tabGroup
      : undefined;
  const tabGroupCommands = tabGroup ? runnable(tabGroup) : [];

  // First word: a group, a built-in, or on a pod tab a bare command of the tab's group, exec or logs.
  if (settled.length === 0) {
    const bare = context.podTab
      ? [
          ...tabGroupCommands,
          ...runnable('exec'),
          ...Object.keys(IMPLIED_GROUPS),
          ...POD_BUILTINS,
        ]
      : [];
    const words = [...new Set([...groups, ...BUILTINS, ...bare])]
      .filter(word => word.startsWith(partial))
      .sort();
    return settle(head, words, partial);
  }

  // Resolve which group and command the settled words name, applying the pod tab's implied group.
  const [first] = settled;
  const impliedGroup = context.podTab
    ? impliedGroupFor(first, tabGroupCommands, tabGroup, runnable)
    : undefined;
  let group: string | undefined;
  let commandName: string | undefined;
  let argumentTokens: string[];
  if (groups.includes(first)) {
    group = first;
    commandName = settled[1];
    argumentTokens = settled.slice(2);
  } else if (context.podTab && impliedGroup) {
    group = impliedGroup;
    commandName = first;
    argumentTokens = settled.slice(1);
  } else if (first === 'help') {
    return settled.length === 1
      ? settle(
          head,
          groups.filter(name => name.startsWith(partial)),
          partial,
        )
      : noCompletion(line);
  } else {
    return noCompletion(line);
  }

  // Second word inside a known group: its command.
  if (!commandName) {
    const names = commands
      .filter(
        command =>
          command.category === group && command.command.startsWith(partial),
      )
      .map(command => command.command);
    return settle(head, names, partial);
  }

  if (group === 'builtin') {
    if (
      commandName !== 'cd' ||
      argumentTokens.length ||
      !context.listPodDirectory
    )
      return noCompletion(line);
    const directoriesOnly = async (directory: string) =>
      (await context.listPodDirectory!(directory)).filter(entry =>
        entry.endsWith('/'),
      );
    return completePath(head, partial, directoriesOnly);
  }

  const spec = commands.find(
    command => command.category === group && command.command === commandName,
  );
  if (!spec) return noCompletion(line);
  const options = {
    ...optionsFromUsage(spec.usage),
    ...(spec.node
      ? Object.fromEntries(NODE_OPTIONS.map(name => [name, []]))
      : {}),
  };

  // The value of the option just before the cursor, when it has a fixed set.
  const previous = argumentTokens[argumentTokens.length - 1];
  if (previous?.startsWith('--')) {
    const choices =
      previous === '--node'
        ? (context.nodeNames ?? [])
        : previous === '--self-managed-name'
          ? (context.selfManagedNames ?? [])
          : (options[previous] ?? []);
    if (choices.length)
      return settle(
        head,
        choices.filter(choice => choice.startsWith(partial)).sort(),
        partial,
      );
  }

  if (partial.startsWith('-')) {
    return settle(
      head,
      Object.keys(options)
        .filter(name => name.startsWith(partial))
        .sort(),
      partial,
    );
  }

  // Anything else is a positional, and the only positionals worth completing are paths.
  if (POD_GROUPS.has(group) && context.listPodDirectory)
    return completePath(head, partial, context.listPodDirectory);
  if (BUCKET_GROUPS.has(group) && context.listBucketDirectory) {
    const bucketGroup = group;
    return completePath(head, partial, directory =>
      context.listBucketDirectory!(bucketGroup, directory),
    );
  }
  return noCompletion(line);
}

/** Entries from `ls -1pA` output: one name per line, directories with a trailing slash. */
export const parsePodListing = (output: string) =>
  output
    .split('\n')
    .map(entry => entry.trim())
    .filter(entry => entry && !entry.startsWith('→ '));

/** Entries from a bucket `ls`: files print as `<key> <size> MB`, directories as `<key>/`. */
export const parseBucketListing = (output: string) =>
  output
    .split('\n')
    .map(entry => entry.trim())
    .filter(
      entry => entry && !entry.startsWith('Error:') && !entry.startsWith('→ '),
    )
    .map(entry => {
      const isDirectory = entry.endsWith('/');
      const key = isDirectory ? entry.slice(0, -1) : entry.split(/\s{2,}/)[0];
      const name = key.slice(key.lastIndexOf('/') + 1);
      return isDirectory ? `${name}/` : name;
    })
    .filter(Boolean);

export function describeGroups(commands: CliCommandSpec[]) {
  if (!commands.length) return 'No commands available.';

  const lines = uniqueGroups(commands).map(group => {
    const inGroup = commands.filter(command => command.category === group);
    const runnable = inGroup.filter(command => command.allowed).length;
    return `  ${group.padEnd(22)}${inGroup.length} commands (${runnable} you can run)`;
  });

  return [
    'Command groups:',
    ...lines,
    '',
    'Terminal built-ins: `cd DIR` and `pwd` set and show where pod commands run, `clear` wipes the screen.',
    'Run `help <group>` to list its commands.',
  ].join('\n');
}

export function helpForGroup(commands: CliCommandSpec[], group: string) {
  const inGroup = commands.filter(command => command.category === group);
  if (!inGroup.length) {
    return `No command group named "${group}". Groups: ${uniqueGroups(commands).join(', ')}.`;
  }

  const lines = inGroup.map(command => {
    const note = command.unsupported
      ? `  -- not in the browser (${command.unsupported})`
      : command.allowed
        ? ''
        : `  -- needs ${command.permission} permission`;
    return `  ${command.usage.padEnd(46)}${command.summary}${note}`;
  });

  return [`${group} commands:`, ...lines].join('\n');
}
